/* eslint-disable indent */
import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react'
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { addCircleOutline, checkmarkSharp, chevronBackOutline, trash, trashOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { useHistory } from 'react-router'
import './SignerAndSecurityQuestions.component.css'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { IOption } from '../../../common/common.types'
import { useDispatch, useSelector } from 'react-redux'
import { getSignersSelector } from '../selectors'
import { ISigner } from '../store/completeChart.types'
import {
  addSigner,
  deleteSigner,
  saveSigner,
  setAnswer1,
  setAnswer2,
  setCurrentSigner,
  setPin,
  setQuestionList,
  setSignChartAs,
  setUser,
  setVerifyCrew,
  setSignerList,
  setClinicalUser,
  setExtraSecurityId,
} from '../store/completeChart.actions'
import { getCrewListSelector } from '../../crew/selectors'
import { showAlert } from '../../vitals/store/vitals.actions'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { hashPin } from '../../login/util/login.util'
import { CrewService } from '../../crew/services/crew.service'
import { ICrewListMember } from '../../pcr/models/crewList.V1'
import { getUserSelector } from '../../user/selectors'
import { ITimelineState } from '../../timeline/store/timeline.types'
import { getPatientTimelineSelector } from '../../timeline/selectors/patientTimeline.selector'
import { hashAnswer1, hashAnswer2 } from '../utils/hasAnswers.utils'
import LockUploadBtn from './LockUploadBtn.component'
import { useAutofillGuardName } from '../../../hooks'
interface Medication {
  medicationName: string[]
}

const SignerAndSecurityQuestions: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const [medication, setMedication] = useState<Medication[]>([])
  const signerState = useSelector(getSignersSelector)
  const { pin } = signerState
  const crewList = useSelector(getCrewListSelector)
  const timeline: ITimelineState = useSelector(getPatientTimelineSelector)
  const signerFieldsAutofillGuardName = useAutofillGuardName('signer')

  const crewOptions = crewList.map((item) => item.userId)
  const signChartAsOption = [
    {
      id: 15455,
      name: 'Crew',
    },
  ]
  const { signerList, clinicalUserList, securityQuestionAnswerList } = signerState
  const serviceConfig: IServiceConfig = useSelector(getServiceConfigSelector)
  const userData = useSelector(getUserSelector)

  const { PR_SIGNATURES, USER_SECURITY_SCHEME, EXTRA_SECURITY_STATUS } = serviceConfig

  const prevSignerListRef = useRef<ISigner[] | null>(null)

  useEffect(() => {
    if (signerList?.length > 0 && clinicalUserList?.length > 0) {
      const updatedSignerList = signerList?.map((signer) => {
        if (signer?.user?.id) {
          const clinicalUser = clinicalUserList?.find((user) => user?.id === signer?.user?.id)
          const isEligible = clinicalUser?.isClinical === 1 && clinicalUser?.signChart === 1

          return { ...signer, isClinicalUser: isEligible }
        }
        return signer
      })

      const prevSignerList = prevSignerListRef.current
      if (JSON.stringify(prevSignerList) !== JSON.stringify(updatedSignerList)) {
        dispatch(setSignerList(updatedSignerList))
        prevSignerListRef.current = updatedSignerList
      }
    }
  }, [signerList, clinicalUserList, dispatch])

  useEffect(() => {
    if (timeline?.items?.length === 0) {
      setMedication([])
    }
    const medications: ((prevState: Medication[]) => Medication[]) | { medicationName: any }[] = []

    timeline.items.forEach((item) => {
      const eventSubHeader = item.eventSubHeader || ''

      const cleanedEventSubHeader = eventSubHeader
        ?.replace(/Epinephrine 1:1,000/g, 'Epinephrine 1:1 000')
        .replace(/Epinephrine 1:10,000/g, 'Epinephrine 1:10 000')

      const parts: any = cleanedEventSubHeader?.split(' By ')
      for (let i = 0; i < parts?.length - 1; i++) {
        const lastValue = parts[i]?.split(',').pop()?.trim()
        if (lastValue) {
          medications?.push({ medicationName: lastValue })
        }
      }
    })

    const updatedMedications = medications.map((med) => ({
      medicationName:
        med.medicationName === 'Epinephrine 1:1 000'
          ? 'Epinephrine 1:1,000'
          : med.medicationName === 'Epinephrine 1:10 000'
          ? 'Epinephrine 1:10,000'
          : med.medicationName,
    }))
    setMedication(updatedMedications)
  }, [timeline.items, JSON.stringify(timeline.items.map((item) => item.eventSubHeader))])

  const setUserOnChange = (option: IOption, signer: ISigner) => {
    const userQuestions = securityQuestionAnswerList?.filter((item) => item.id === option.id)
    if (userQuestions) {
      const shuffledQuestions = [...userQuestions]?.sort(() => Math.random() - 0.5)
      const randomQuestions = shuffledQuestions?.slice(0, 2)
      dispatch(setQuestionList(randomQuestions, signer))
    }
    dispatch(setUser(option, signer))

    const selectedSignChartAsOption = signChartAsOption?.find((item) => item.name === 'Crew')
    if (signChartAsOption) {
      dispatch(setSignChartAs(selectedSignChartAsOption, signer))
    }
    const clinicalUser = clinicalUserList?.find((user) => user?.id === option.id)
    // Check if both is_clinical and sign_chart are 1
    const isEligible = clinicalUser?.isClinical === 1 && clinicalUser?.signChart === 1
    dispatch(setClinicalUser(isEligible, signer))
    save()
  }

  const setSignChartAsOnChange = (option: IOption, signer: ISigner) => {
    dispatch(setSignChartAs(option, signer))
    save()
  }

  const onPinChange = (e: CustomEvent, signer: ISigner) => {
    e.preventDefault()
    dispatch(setPin(e.detail.value, signer))
    save()
  }

  const setAnswer1OnChange = (e: CustomEvent, signer: ISigner) => {
    e.preventDefault()
    dispatch(setAnswer1(e.detail.value, signer))
    save()
  }

  const setAnswer2OnChange = (e: CustomEvent, signer: ISigner) => {
    e.preventDefault()
    dispatch(setAnswer2(e.detail.value, signer))
    save()
  }

  const save = () => {
    dispatch(saveSigner())
  }

  const verifyPin = async (pin: string, serviceId: string, userId: string): Promise<boolean> => {
    const inputPin = await hashPin(pin, serviceId, userId)
    const crewService = new CrewService(serviceId)
    const serviceUsers = await crewService.getCrewList()

    if (serviceUsers) {
      const entity = serviceUsers.entity as Array<ICrewListMember>
      const userObj = entity.find((user) => Number(user.userId) === Number(userId))

      if (userObj) {
        const { pin: clientDataPin } = userObj

        if (clientDataPin?.toUpperCase() === inputPin.toUpperCase()) {
          return true
        }
      }
    }
    dispatch(showAlert({ message: t('completeChart.incorrectPINTryAgain') }))
    return false
  }

  const verifyAnswers = async (answer1: string, answer2: string, questionList: any): Promise<boolean> => {
    const inputHash1 = await hashAnswer1(answer1)
    const givenHash1 = await hashAnswer1(questionList[0].answer)
    const inputHash2 = await hashAnswer2(answer2)
    const givenHash2 = await hashAnswer2(questionList[1].answer)
    if (inputHash1?.toUpperCase() === givenHash1.toUpperCase()) {
      if (inputHash2?.toUpperCase() === givenHash2.toUpperCase()) return true
    }
    dispatch(showAlert({ message: t('completeChart.incorrectAnswersTryAgain') }))
    return false
  }

  const onVerifyCrew = async (signer: ISigner) => {
    let answerVerified = false
    const pinVerified = await verifyPin(pin, userData?.serviceId || '', signer.user?.id?.toString() || '')

    if (signer?.answer1 && signer?.answer2) {
      answerVerified = await verifyAnswers(signer?.answer1, signer?.answer2, signer?.questionList || [])
      if (answerVerified) dispatch(setExtraSecurityId(USER_SECURITY_SCHEME, signer))
    }
    if (pinVerified && (medication.length === 0 || signer.questionList.length === 0)) {
      dispatch(setVerifyCrew(true, signer))
    }
    if (pinVerified && !answerVerified && medication.length > 0 && EXTRA_SECURITY_STATUS === 0) {
      dispatch(setVerifyCrew(true, signer))
    }
    if (pinVerified && answerVerified) {
      dispatch(setVerifyCrew(true, signer))
    }
    if (pinVerified && !answerVerified && medication.length > 0 && EXTRA_SECURITY_STATUS === 1) {
      dispatch(showAlert({ message: t('completeChart.configureSecurityQuestion') }))
    }
  }

  const onAddSigner = () => {
    if (signerList?.length < crewOptions?.length) {
      const firstAvailableUser = crewOptions?.find((userId) => !signerList?.some((signer) => signer.user === userId))

      dispatch(addSigner())
      save()

      if (firstAvailableUser) {
        dispatch(setUser({ id: firstAvailableUser, name: firstAvailableUser }, signerList[signerList.length]))
      }

      dispatch(setCurrentSigner(null))
    }
  }

  const onDeleteSigner = (signer: ISigner) => {
    const alertState = {
      header: t('completeChart.deleteHeader'),
      message: t('completeChart.deleteSigner'),
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: () => handleDelete(signer) }],
    }
    dispatch(showAlert(alertState))
  }

  const handleDelete = (signer: ISigner) => {
    dispatch(deleteSigner(signer))
    save()
    dispatch(setCurrentSigner(null))
  }

  const handleBack = () => {
    const updatedSignerList = signerList.filter(
      (signer) =>
        signer.user !== null ||
        signer.signChartAs !== null ||
        signer.pin.trim() !== '' ||
        signer.answer1.trim() !== '' ||
        signer.answer2.trim() !== ''
    )
    dispatch(setSignerList(updatedSignerList))
    history.push(TabConstants.COMPLETE_CHART_TAB)
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (signerList.length > 0 && signerList.some((signer) => !signer.verified)) {
      const alertState = {
        header: 'Warning',
        message: t('completeChart.unsavedSignatureData'),
        buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: handleBack }],
      }
      dispatch(showAlert(alertState))
    } else {
      handleBack()
    }
  }

  return (
    <IonPage id='SignerPage' data-testid='Signer'>
      <PCRHeader />
      <IonHeader className='ion-no-padding'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('completeChart.completeChart')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <h5 className='px-25 text-align-center'>
          {' '}
          {t('completeChart.headingStart')} <b>{t('completeChart.headingMiddle')}</b> {t('completeChart.headingEnd')}
        </h5>
        <IonList className='list-background'>
          {signerList.map((signer: ISigner, index: number) => (
            <IonItemSliding key={signer.key}>
              <IonCard id={'id'} className='card-margin ion-no-margin' data-testid='SignerCard'>
                <IonCardContent>
                  <IonGrid className='no-padding mb-1'>
                    <IonRow>
                      <IonCol>
                        {' '}
                        <span className='fs-18 heading'>
                          {t('completeChart.signer')} {index + 1}
                        </span>
                      </IonCol>
                      <IonButtons>
                        <IonButton data-testid='deleteSigner' fill='default' onClick={() => onDeleteSigner(signer)}>
                          <IonIcon icon={trashOutline} />
                        </IonButton>
                      </IonButtons>
                    </IonRow>

                    <IonRow>
                      <IonCol className='picklist'>
                        <SingleSelect
                          label={t('completeChart.user')}
                          listHeader={t('completeChart.user')}
                          dataTestID='user'
                          options={crewOptions}
                          action={setUserOnChange}
                          selectedOption={signer.user}
                          dispatchData={signer}
                          disabled={signer.verified}
                        />
                      </IonCol>
                    </IonRow>

                    <hr className='field-divider' />

                    <IonRow>
                      <IonCol className='picklist'>
                        <SingleSelect
                          label={t('completeChart.signChartAs')}
                          listHeader={t('completeChart.signChartAs')}
                          dataTestID='signChartAs'
                          options={signChartAsOption}
                          action={setSignChartAsOnChange}
                          selectedOption={signer.signChartAs}
                          dispatchData={signer}
                          disabled={signer.verified}
                        />
                      </IonCol>
                    </IonRow>

                    <hr className='field-divider' />
                    {!signer.verified && (
                      <>
                        {PR_SIGNATURES !== 0 && (
                          <>
                            <IonRow className='margin-negative'>
                              <IonCol>
                                <IonItem>
                                  <IonInput
                                    data-testid='Pin'
                                    placeholder={t('completeChart.pin')}
                                    id='pin'
                                    name={`${signerFieldsAutofillGuardName}-pin-${index}`}
                                    type='password'
                                    autocomplete='new-password'
                                    value={signer.pin}
                                    onIonChange={(e) => onPinChange(e, signer)}
                                  />
                                </IonItem>
                              </IonCol>
                            </IonRow>

                            <hr className='field-divider' />
                          </>
                        )}
                        {signer.user &&
                          signer.questionList.length > 0 &&
                          EXTRA_SECURITY_STATUS === 1 &&
                          medication.length > 0 && (
                            <>
                              <IonRow>
                                {' '}
                                <span className='fs-18 mt-1 heading'>
                                  {t('completeChart.additionalSecurityQuestion')}
                                </span>
                              </IonRow>
                              <IonRow className='mt-1'>
                                <IonLabel>{signer.questionList[0]?.question}</IonLabel>
                              </IonRow>
                              <IonRow>
                                <IonItem>
                                  <IonInput
                                    data-testid='answer1'
                                    id='answer1'
                                    name={`${signerFieldsAutofillGuardName}-answer1-${index}`}
                                    type='password'
                                    autocomplete='new-password'
                                    value={signer.answer1}
                                    onIonChange={(e) => setAnswer1OnChange(e, signer)}
                                  />
                                </IonItem>
                              </IonRow>
                              <hr className='field-divider' />
                              <IonRow className='mt-1'>
                                <IonLabel>{signer.questionList[1]?.question}</IonLabel>
                              </IonRow>
                              <IonRow>
                                <IonItem>
                                  <IonInput
                                    data-testid='answer2'
                                    id='answer2'
                                    name={`${signerFieldsAutofillGuardName}-answer2-${index}`}
                                    type='password'
                                    autocomplete='new-password'
                                    value={signer.answer2}
                                    onIonChange={(e) => setAnswer2OnChange(e, signer)}
                                  />
                                </IonItem>
                              </IonRow>
                              <hr className='field-divider' />
                            </>
                          )}
                      </>
                    )}
                  </IonGrid>
                  {signer?.isClinicalUser === false && (
                    <IonLabel className='d-flex justify-content-center' color={'danger'}>
                      {t('completeChart.unableToSignChart')}
                    </IonLabel>
                  )}
                  {!signer.verified && signer?.isClinicalUser && (
                    <IonButton
                      disabled={
                        medication.length > 0 && signer.questionList.length > 0 && EXTRA_SECURITY_STATUS === 1
                          ? !(signer.pin && signer.answer1 && signer.answer2) // If medication is selected, require pin + answers
                          : !signer.pin // Otherwise, only pin is required
                      }
                      className='save-button mt-1'
                      color={'primary'}
                      onClick={() => onVerifyCrew(signer)}>
                      {t('completeChart.verify')}
                    </IonButton>
                  )}
                  {signer.verified && signer?.isClinicalUser && (
                    <IonButton className='save-button mt-1' color='primary'>
                      {t('completeChart.verified')}
                      <IonIcon className='check-icon' icon={checkmarkSharp} />
                    </IonButton>
                  )}
                </IonCardContent>
              </IonCard>
              <IonItemOptions side='end' className='delete-swipe'>
                <IonItemOption
                  data-testid='DeleteIcon'
                  color='danger'
                  onDrag={() => onDeleteSigner(signer)}
                  onClick={() => onDeleteSigner(signer)}>
                  <IonIcon slot='icon-only' icon={trash} />
                </IonItemOption>
              </IonItemOptions>
            </IonItemSliding>
          ))}
          {signerList.length < crewOptions.length && (
            <IonRow className='ion-float-right'>
              <IonButton
                data-testid='AddMedication'
                className='transform-none'
                fill='clear'
                color='primary-alt'
                onClick={onAddSigner}>
                <IonIcon icon={addCircleOutline} />
                <IonLabel>{t('completeChart.addSigner')}</IonLabel>
              </IonButton>
            </IonRow>
          )}
        </IonList>
        {medication.length > 0 && USER_SECURITY_SCHEME === 1 && (
          <>
            <IonRow className='mt-1'>
              <IonLabel className='font-roboto-condensed p-20'>{t('completeChart.medTitle')} </IonLabel>
            </IonRow>
            <IonRow>
              <ul>
                {medication.map((med, index) => (
                  <li className='mb-5' key={index}>
                    {med.medicationName}
                  </li>
                ))}
              </ul>
            </IonRow>
          </>
        )}
      </IonContent>

      <LockUploadBtn isOnSignerPage={true} />
    </IonPage>
  )
}

export default SignerAndSecurityQuestions
